import { DraftingEditSuggestionsRequest } from '@harvey-frontend/openapi/models/DraftingEditSuggestionsRequest'
import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { useAbortController } from 'providers/abort-controller-provider'
import { v4 as uuidv4 } from 'uuid'
import { useShallow } from 'zustand/react/shallow'

import { DraftingEditSuggestion } from 'openapi/models/DraftingEditSuggestion'
import { KnowledgeSourceType } from 'openapi/models/KnowledgeSourceType'
import { UploadedFile } from 'openapi/models/UploadedFile'

import useQueryAnalytics from 'components/common/analytics/use-query-analytics'

import { fetchDraftingSuggestStream } from '../../../services/endpoints'
import { EVENT_KIND } from '../../../utils/constants'
import { useAccessTokenContext } from '../providers/AccessTokenProvider'
import useAppStore from '../stores/store'
import { ensureError } from '../utils/error-utils'
import { getTextAroundCursor } from '../utils/formatting-utils'
import { useRequestMode } from './useRequestMode'

export const useFetchSuggestions = (precedents: UploadedFile[]) => {
  const { addAbortController } = useAbortController()
  const { getAccessToken } = useAccessTokenContext()
  const mode = useRequestMode()
  const {
    documentId,
    eventId,
    highlightedText,
    instruction,
    addSuggestion,
    setQueryError,
    setQueryCompleted,
    setQueryId,
    setSuggestions,
  } = useAppStore(
    useShallow((s) => ({
      documentId: s.documentId,
      eventId: s.eventId,
      highlightedText: s.highlightedText,
      instruction: s.instruction,
      setQueryCompleted: s.setQueryCompleted,
      setSuggestions: s.setSuggestions,
      setQueryError: s.setQueryError,
      addSuggestion: s.addSuggestion,
      setQueryId: s.setQueryId,
    }))
  )
  const {
    recordQuerySubmitted,
    recordQueryCancel,
    recordQueryCompletion,
    recordQueryError,
  } = useQueryAnalytics(EVENT_KIND)

  return async () => {
    const queryId = uuidv4()
    setQueryId(queryId)
    try {
      const accessToken = await getAccessToken()
      if (!highlightedText || !accessToken) {
        return
      }
      setQueryError(null)
      setQueryCompleted(false)
      setSuggestions([], true)
      const abortController = new AbortController()
      addAbortController(mode, abortController)
      const documentContext = await getTextAroundCursor()
      const request: DraftingEditSuggestionsRequest = {
        instructions: instruction,
        wordDocumentId: documentId,
        eventId,
        textToEdit: highlightedText.text,
        documentContext,
      }

      if (precedents.length > 0) {
        request.knowledgeSources = [
          {
            type: KnowledgeSourceType.FILES,
            fileIds: precedents.map((precedent) => precedent.id),
          },
        ]
      }

      let changeListResponse: DraftingEditSuggestion[] = []
      recordQuerySubmitted({
        type: mode,
        query_id: queryId,
        clause_length: highlightedText.text.length,
        instruction_length: instruction.length,
        precedents_count: precedents.length,
      })
      // Attempt to use the streaming endpoint
      changeListResponse = await fetchDraftingSuggestStream({
        accessToken,
        abortController,
        request,
        updateCallback: async (response) => {
          if (!_.isEmpty(response)) {
            addSuggestion({
              ...response,
              range: highlightedText,
              originalText: highlightedText.text,
            })
          }
        },
      })
      if (
        _.isEmpty(changeListResponse) ||
        _.every(changeListResponse, _.isNil)
      ) {
        throw new Error('Empty response from streaming endpoint')
      }

      // Record query completion for streaming success
      recordQueryCompletion({
        type: mode,
        query_id: queryId,
        suggestions_length: changeListResponse.length,
      })
      setQueryCompleted(true)
    } catch (e) {
      const error = ensureError(e)
      if (error.name === 'AbortError') {
        setQueryError({ reason: 'Request was canceled.' })
        recordQueryCancel({
          type: mode,
          query_id: queryId,
          clause_length: highlightedText?.text.length ?? 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
        })
      } else {
        Sentry.captureException(error)
        console.error('Error creating suggestions', error)
        setQueryError({ reason: 'Error creating suggestions' })
        recordQueryError({
          type: mode,
          clause_length: highlightedText?.text.length ?? 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
          query_id: queryId,
        })
      }
    }
  }
}
